/**
 * FS-04 제어 평면 계약(C-07 · Bearer 세션 · 기준 VITE_FIELD_API):
 *   GET /api/runtime-config → {version, config: RuntimeConfig}
 *   GET /api/modes → {modes:[{modeId, version, principleTags, phase, definition}]} · GET /api/modes/{modeId}
 *   GET /api/catalog?type=… · GET /api/courses/{c}/snapshots/{id}/run-bundle → RunBundle
 *   S5: GET /api/courses/{c}/play-settings → {realism, presetKey, timeBudgetS}(교사 설정 중 학생에게 필요한 것만)
 * 셸은 받은 것을 그대로 커널 호스트(C-05)에 넘긴다. 확정 전 봉투는 여기서만 너그럽게 읽는다.
 */
import { useMemo } from "react";
import type { ModeDefinition, RunBundle, RuntimeConfig } from "@picnic-field/local-host/protocol";
import { useSession, useSessionManager } from "../../store/session/SessionProvider";
import type { Requester } from "../../types/api";

export interface ModeEntry {
  modeId: string;
  version: number;
  principleTags: string[];
  phase: string;
  definition: ModeDefinition;
}

/** 학생도 읽는 놀이 설정(S5) — 교사 '현실감'(SR-RUN-06) · 기본 코스 프리셋 · 시간 예산 */
export interface PlaySettings {
  realism: boolean;
  presetKey: string | null;
  timeBudgetS: number;
}

export const DEFAULT_PLAY_SETTINGS: PlaySettings = {
  realism: false,
  presetKey: null,
  timeBudgetS: 600,
};

/** 응답을 너그럽게 — 모르는 값은 기본값 */
export function readPlaySettings(v: unknown): PlaySettings {
  const o = (v ?? {}) as Record<string, unknown>;
  const budget = Number(o.timeBudgetS);
  return {
    realism: o.realism === true,
    presetKey: typeof o.presetKey === "string" && o.presetKey ? o.presetKey : null,
    timeBudgetS: Number.isFinite(budget) && budget > 0 ? budget : DEFAULT_PLAY_SETTINGS.timeBudgetS,
  };
}

export interface CourseApi {
  runtimeConfig(): Promise<RuntimeConfig>;
  modes(): Promise<ModeEntry[]>;
  mode(modeId: string): Promise<ModeEntry>;
  catalog(type: string): Promise<unknown>;
  /** 무대 꾸밈 3D 모델 GLB(카탈로그 stage_model payload.glb · 2026-10-08) — blob URL 로 렌더러에 */
  stageModel(sha256: string): Promise<Uint8Array>;
  runBundle(snapshotId: string): Promise<RunBundle>;
  /** 못 받으면 기본값(현실감 끔 · 프리셋 없음) — 놀이는 계속된다 */
  playSettings(): Promise<PlaySettings>;
}

/** 서버가 설정을 못 주면 이 값으로(ARC §7.5 · SRS 기본값) */
export const DEFAULT_RUNTIME_CONFIG: RuntimeConfig = {
  tick: { hz: 60, substeps: 4 },
  program: { opBudget: 500, opBudgetMax: 2000, watchdogMs: 2000, memoryMb: 4 },
  local: { maxFriends: 5, maxGhosts: 3 },
  input: { maxHz: 30 },
};

export const COURSE_MODE_ID = "obstacle-course";

const modeOf = (v: unknown): ModeEntry => {
  const o = (v ?? {}) as Partial<ModeEntry> & { definition?: ModeDefinition };
  const def = (o.definition ?? (v as ModeDefinition)) as ModeDefinition;
  return {
    modeId: o.modeId ?? def.id,
    version: o.version ?? def.version,
    principleTags: [...(o.principleTags ?? def.principleTags ?? [])],
    phase: o.phase ?? def.phase,
    definition: def,
  };
};

export function createCourseApi(request: Requester, courseId: string): CourseApi {
  const c = `/api/courses/${encodeURIComponent(courseId)}`;
  return {
    async runtimeConfig() {
      const v = await request<{ config?: RuntimeConfig } | RuntimeConfig>("/api/runtime-config");
      const cfg = (v as { config?: RuntimeConfig }).config ?? (v as RuntimeConfig);
      return {
        ...DEFAULT_RUNTIME_CONFIG,
        ...cfg,
        local: { ...DEFAULT_RUNTIME_CONFIG.local, ...cfg?.local },
        input: { ...DEFAULT_RUNTIME_CONFIG.input, ...cfg?.input },
      };
    },
    async modes() {
      const v = await request<{ modes?: unknown[] } | unknown[]>("/api/modes");
      const list = Array.isArray(v) ? v : (v.modes ?? []);
      return list.map(modeOf);
    },
    async mode(modeId) {
      return modeOf(await request<unknown>(`/api/modes/${encodeURIComponent(modeId)}`));
    },
    catalog: (type) => request<unknown>(`/api/catalog?type=${encodeURIComponent(type)}`),
    stageModel: (sha) =>
      request<Uint8Array>(`/api/stage-models/${encodeURIComponent(sha)}`, {
        as: "bytes",
      } as RequestInit),
    runBundle: (id) => request<RunBundle>(`${c}/snapshots/${encodeURIComponent(id)}/run-bundle`),
    playSettings: () =>
      request<unknown>(`${c}/play-settings`).then(readPlaySettings, () => ({
        ...DEFAULT_PLAY_SETTINGS,
      })),
  };
}

export function useCourseApi(): CourseApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const courseId = state.status === "ready" ? state.session.courseId : null;
  return useMemo(
    () => (courseId ? createCourseApi(manager.request, courseId) : null),
    [manager, courseId],
  );
}
